import { cn } from '@/lib/utils';
import {
    Bot,
    Code2,
    Eye,
    FolderOpen,
    Play,
    Plus,
    RefreshCw,
    Rocket,
    Save,
    Search,
    Settings,
    Shield,
    Terminal,
    TestTubes,
    Trash2,
    Wand2,
    Zap,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/* ================================================================== */
/*  Command types                                                     */
/* ================================================================== */

export interface Command {
  id: string;
  label: string;
  description?: string;
  icon: React.ElementType;
  category: string;
  keybinding?: string;
  action: () => void;
}

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  commands: Command[];
}

/* ================================================================== */
/*  Component                                                         */
/* ================================================================== */

export default function CommandPalette({ isOpen, onClose, commands }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus on open
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Filter and score commands
  const filtered = useMemo(() => {
    if (!query.trim()) return commands;
    const q = query.toLowerCase();
    return commands
      .map((cmd) => {
        const label = cmd.label.toLowerCase();
        const desc = (cmd.description ?? '').toLowerCase();
        const cat = cmd.category.toLowerCase();
        let score = 0;
        if (label.startsWith(q)) score = 100;
        else if (label.includes(q)) score = 80;
        else if (desc.includes(q)) score = 60;
        else if (cat.includes(q)) score = 40;
        else {
          // fuzzy: check if all chars appear in order
          let qi = 0;
          for (const ch of label) {
            if (qi < q.length && ch === q[qi]) qi++;
          }
          if (qi === q.length) score = 20;
        }
        return { cmd, score };
      })
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.cmd);
  }, [query, commands]);

  // Group by category
  const grouped = useMemo(() => {
    const map = new Map<string, Command[]>();
    for (const cmd of filtered) {
      const arr = map.get(cmd.category) ?? [];
      arr.push(cmd);
      map.set(cmd.category, arr);
    }
    return map;
  }, [filtered]);

  // Keep selected index in bounds
  useEffect(() => {
    if (selectedIndex >= filtered.length) setSelectedIndex(Math.max(0, filtered.length - 1));
  }, [filtered.length, selectedIndex]);

  // Scroll selected into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${selectedIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  const executeCommand = useCallback(
    (cmd: Command) => {
      onClose();
      // Small delay so the palette closes first
      setTimeout(() => cmd.action(), 50);
    },
    [onClose],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((i) => Math.min(i + 1, filtered.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          if (filtered[selectedIndex]) executeCommand(filtered[selectedIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [filtered, selectedIndex, executeCommand, onClose],
  );

  // Global escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  let globalIdx = 0;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      <div
        className="w-[520px] max-h-[400px] bg-[#252526] rounded-lg border border-[#454545] shadow-2xl flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-2 px-3 py-2 border-b border-[#454545]">
          <Search className="w-4 h-4 text-[#858585] shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command..."
            className="flex-1 bg-transparent text-sm text-[#d4d4d4] placeholder-[#858585] outline-none"
          />
          <kbd className="text-[9px] text-[#666] bg-[#1e1e1e] px-1.5 py-0.5 rounded border border-[#333]">
            ESC
          </kbd>
        </div>

        {/* Command list */}
        <div ref={listRef} className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-xs text-[#858585]">No matching commands</div>
          ) : (
            Array.from(grouped.entries()).map(([category, cmds]) => (
              <div key={category}>
                <div className="px-3 py-1 text-[9px] font-semibold uppercase tracking-wider text-[#666]">
                  {category}
                </div>
                {cmds.map((cmd) => {
                  const idx = globalIdx++;
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      data-idx={idx}
                      onClick={() => executeCommand(cmd)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors',
                        idx === selectedIndex
                          ? 'bg-[#04395e] text-white'
                          : 'text-[#cccccc] hover:bg-[#2a2d2e]',
                      )}
                    >
                      <Icon className="w-4 h-4 shrink-0 text-[#858585]" />
                      <div className="flex-1 min-w-0">
                        <span className="text-xs">{cmd.label}</span>
                        {cmd.description && (
                          <span className="ml-2 text-[10px] text-[#858585]">{cmd.description}</span>
                        )}
                      </div>
                      {cmd.keybinding && (
                        <kbd className="text-[9px] text-[#858585] bg-[#1e1e1e] px-1.5 py-0.5 rounded border border-[#333] shrink-0">
                          {cmd.keybinding}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-3 py-1 border-t border-[#454545] text-[9px] text-[#666]">
          <span>
            {filtered.length} command{filtered.length !== 1 ? 's' : ''}
          </span>
          <span>↑↓ Navigate · ↵ Select</span>
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Default IDE commands builder                                      */
/* ================================================================== */

export function buildIDECommands(actions: {
  compile?: () => void;
  deploy?: () => void;
  newFile?: () => void;
  saveFile?: () => void;
  toggleTerminal?: () => void;
  togglePreview?: () => void;
  toggleSidebar?: () => void;
  openExplorer?: () => void;
  openDeploy?: () => void;
  openAI?: () => void;
  openSettings?: () => void;
  clearTerminal?: () => void;
  aiGenerate?: () => void;
  aiExplain?: () => void;
  aiAudit?: () => void;
  aiGasOptimize?: () => void;
  aiGenerateTests?: () => void;
  refreshFiles?: () => void;
}): Command[] {
  const cmds: Command[] = [];

  const add = (
    id: string,
    label: string,
    icon: React.ElementType,
    category: string,
    action: (() => void) | undefined,
    desc?: string,
    keybinding?: string,
  ) => {
    if (action) cmds.push({ id, label, description: desc, icon, category, keybinding, action });
  };

  // File
  add('file.new', 'New File', Plus, 'File', actions.newFile, 'Create a new file');
  add('file.save', 'Save File', Save, 'File', actions.saveFile, 'Save the current file', 'Ctrl+S');
  add(
    'file.refresh',
    'Refresh File Tree',
    RefreshCw,
    'File',
    actions.refreshFiles,
    'Reload files from workspace',
  );

  // Build
  add(
    'build.compile',
    'Compile Contract',
    Play,
    'Build',
    actions.compile,
    'Compile the active Solidity file',
    'Ctrl+Shift+B',
  );
  add(
    'build.deploy',
    'Deploy Contract',
    Rocket,
    'Build',
    actions.deploy,
    'Deploy the compiled contract',
  );

  // AI
  add(
    'ai.generate',
    'AI: Generate Contract',
    Wand2,
    'AI',
    actions.aiGenerate,
    'Generate a smart contract with AI',
  );
  add('ai.explain', 'AI: Explain Code', Code2, 'AI', actions.aiExplain, 'Explain the current code');
  add('ai.audit', 'AI: Security Audit', Shield, 'AI', actions.aiAudit, 'Run a security audit');
  add('ai.gas', 'AI: Gas Optimize', Zap, 'AI', actions.aiGasOptimize, 'Analyze gas usage');
  add(
    'ai.tests',
    'AI: Generate Tests',
    TestTubes,
    'AI',
    actions.aiGenerateTests,
    'Generate unit tests',
  );

  // View
  add(
    'view.terminal',
    'Toggle Terminal',
    Terminal,
    'View',
    actions.toggleTerminal,
    undefined,
    'Ctrl+`',
  );
  add('view.preview', 'Toggle Preview', Eye, 'View', actions.togglePreview);
  add('view.explorer', 'Show Explorer', FolderOpen, 'View', actions.openExplorer);
  add('view.deploy', 'Show Deploy Panel', Rocket, 'View', actions.openDeploy);
  add('view.ai', 'Show AI Assistant', Bot, 'View', actions.openAI);
  add('view.settings', 'Show Settings', Settings, 'View', actions.openSettings);

  // Terminal
  add('terminal.clear', 'Clear Terminal', Trash2, 'Terminal', actions.clearTerminal);

  return cmds;
}
